import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Store, Clock, Loader2, CheckCircle2, Package, Truck, MapPin } from 'lucide-react';

// â”€â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

interface OrderItem {
  name: string;
  quantity: number;
  unit: string;
  price: number;
  total: number;
}

interface OrderStatus {
  id: string;
  status: string;
  customerName: string;
  deliveryType: string;
  items: OrderItem[];
  subtotal: number;
  deliveryFee: number;
  total: number;
  pixCode?: string;
  pixQrCode?: string;
  createdAt: string;
}

// â”€â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

const API_BASE = import.meta.env.VITE_API_URL || '';

const STATUS_STEPS = [
  { key: 'criado', label: 'Pedido criado', icon: 'âœ…' },
  { key: 'pago', label: 'Pagamento confirmado', icon: 'ðŸ’³' },
  { key: 'aceito', label: 'Pedido aceito', icon: 'ðŸ‘' },
  { key: 'separando', label: 'Separando produtos', icon: 'ðŸ“¦' },
  { key: 'pronto', label: 'Pronto', icon: 'âœ…' },
  { key: 'saiu_entrega', label: 'Saiu para entrega', icon: 'ðŸ›µ' },
  { key: 'entregue', label: 'Entregue', icon: 'âœ…' },
];

const STATUS_ORDER: Record<string, number> = {
  aguardando_pagamento: 0,
  criado: 0,
  pago: 1,
  aceito: 2,
  separando: 3,
  pronto: 4,
  saiu_entrega: 5,
  entregue: 6,
  cancelado: -1,
};

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function getStatusBadgeColor(status: string): string {
  switch (status) {
    case 'aguardando_pagamento':
      return 'bg-yellow-100 text-yellow-800';
    case 'pago':
    case 'aceito':
      return 'bg-blue-100 text-blue-800';
    case 'separando':
      return 'bg-orange-100 text-orange-800';
    case 'pronto':
      return 'bg-green-100 text-green-800';
    case 'saiu_entrega':
      return 'bg-purple-100 text-purple-800';
    case 'entregue':
      return 'bg-green-100 text-green-800';
    case 'cancelado':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    aguardando_pagamento: 'Aguardando Pagamento',
    criado: 'Pedido Criado',
    pago: 'Pago',
    aceito: 'Aceito',
    separando: 'Separando',
    pronto: 'Pronto',
    saiu_entrega: 'Saiu para Entrega',
    entregue: 'Entregue',
    cancelado: 'Cancelado',
  };
  return labels[status] || status;
}

// â”€â”€â”€ Main Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export function OrderTrackingPage() {
  const { slug, orderId } = useParams<{ slug: string; orderId: string }>();
  const [order, setOrder] = React.useState<OrderStatus | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const fetchOrder = React.useCallback(async () => {
    if (!slug || !orderId) return;
    try {
      const res = await fetch(`${API_BASE}/api/store/${slug}/orders/${orderId}/status`);
      if (!res.ok) {
        setError('Pedido nÃ£o encontrado');
        return;
      }
      const data = await res.json();
      setOrder({
        id: orderId!,
        status: data.status,
        customerName: data.customer_name,
        deliveryType: data.delivery_type,
        items: (data.items || []).map((i: Record<string, unknown>) => ({
          name: i.name as string,
          quantity: Number(i.quantity),
          unit: i.unit as string,
          price: Number(i.price),
          total: Number(i.subtotal),
        })),
        subtotal: Number(data.total) - Number(data.delivery_fee || 0),
        deliveryFee: Number(data.delivery_fee || 0),
        total: Number(data.total),
        pixCode: data.pix?.payload,
        createdAt: data.timeline?.created || '',
      });
    } catch {
      setError('Erro ao carregar pedido');
    } finally {
      setLoading(false);
    }
  }, [slug, orderId]);

  // Initial fetch
  React.useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  // Auto-refresh every 10 seconds
  React.useEffect(() => {
    const interval = setInterval(fetchOrder, 10000);
    return () => clearInterval(interval);
  }, [fetchOrder]);

  // â”€â”€â”€ Loading / Error â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  if (loading) {
    return (
      <div className="store-page flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="store-page flex min-h-screen flex-col items-center justify-center bg-gray-50 p-4">
        <Package className="h-16 w-16 text-gray-300 mb-4" />
        <h1 className="text-xl font-semibold text-gray-700">{error || 'Pedido nÃ£o encontrado'}</h1>
      </div>
    );
  }

  const currentStep = STATUS_ORDER[order.status] ?? 0;
  const isCancelled = order.status === 'cancelado';

  return (
    <div className="store-page min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="mx-auto max-w-md px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-gray-800">Pedido #{order.id.slice(-6)}</h1>
              <p className="text-sm text-gray-500">{order.customerName}</p>
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-medium ${getStatusBadgeColor(order.status)}`}>
              {getStatusLabel(order.status)}
            </span>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-md px-4 pt-6 space-y-6">
        {/* PIX Section (if awaiting payment) */}
        {order.status === 'aguardando_pagamento' && order.pixCode && (
          <div className="rounded-2xl bg-white p-5 shadow-sm">
            <h2 className="text-base font-bold text-gray-800 mb-3">Pagamento via PIX</h2>
            <div className="text-center">
              <p className="text-2xl font-bold text-emerald-700 mb-3">{formatCurrency(order.total)}</p>
              {order.pixQrCode && (
                <img src={order.pixQrCode} alt="QR Code PIX" className="mx-auto mb-3 h-48 w-48" />
              )}
              <p className="text-xs text-gray-500 break-all font-mono bg-gray-50 p-2 rounded">
                {order.pixCode}
              </p>
              <button
                onClick={() => navigator.clipboard.writeText(order.pixCode || '')}
                className="mt-2 text-sm text-emerald-600 font-medium hover:underline"
              >
                Copiar cÃ³digo PIX
              </button>
              <div className="mt-3 flex items-center justify-center gap-2 text-sm text-yellow-600">
                <Clock className="h-4 w-4" />
                <span>Aguardando confirmaÃ§Ã£o...</span>
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <h2 className="text-base font-bold text-gray-800 mb-4">Acompanhamento</h2>

          {isCancelled ? (
            <div className="flex items-center gap-3 rounded-lg bg-red-50 p-3">
              <span className="text-2xl">âŒ</span>
              <div>
                <p className="font-medium text-red-800">Pedido Cancelado</p>
                <p className="text-sm text-red-600">Este pedido foi cancelado.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-0">
              {STATUS_STEPS.map((step, index) => {
                const isCompleted = index <= currentStep;
                const isCurrent = index === currentStep;
                const isLast = index === STATUS_STEPS.length - 1;

                return (
                  <div key={step.key} className="flex gap-3">
                    {/* Vertical line + dot */}
                    <div className="flex flex-col items-center">
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-full text-sm ${
                          isCompleted
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-400'
                        } ${isCurrent ? 'ring-2 ring-emerald-300' : ''}`}
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : (
                          <span className="text-xs">{step.icon}</span>
                        )}
                      </div>
                      {!isLast && (
                        <div
                          className={`w-0.5 flex-1 min-h-[24px] ${
                            index < currentStep ? 'bg-green-300' : 'bg-gray-200'
                          }`}
                        />
                      )}
                    </div>

                    {/* Label */}
                    <div className={`pb-4 ${isCurrent ? 'pt-1' : 'pt-1.5'}`}>
                      <p
                        className={`text-sm font-medium ${
                          isCompleted ? 'text-gray-800' : 'text-gray-400'
                        } ${isCurrent ? 'text-emerald-700' : ''}`}
                      >
                        {step.label}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Order Details */}
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <h2 className="text-base font-bold text-gray-800 mb-3">Detalhes do Pedido</h2>

          <div className="space-y-2 mb-4">
            {order.items.map((item, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-gray-600">
                  {item.quantity}
                  {item.unit === 'kg' ? 'kg' : 'x'} {item.name}
                </span>
                <span className="font-medium text-gray-800">{formatCurrency(item.total)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 pt-3 space-y-1">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Subtotal</span>
              <span>{formatCurrency(order.subtotal)}</span>
            </div>
            {order.deliveryFee > 0 && (
              <div className="flex justify-between text-sm text-gray-600">
                <span>Taxa de entrega</span>
                <span>{formatCurrency(order.deliveryFee)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-bold text-gray-800 pt-1">
              <span>Total</span>
              <span>{formatCurrency(order.total)}</span>
            </div>
          </div>

          {/* Delivery info */}
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-500">
            {order.deliveryType === 'entrega' ? (
              <>
                <Truck className="h-4 w-4" />
                <span>Entrega</span>
              </>
            ) : (
              <>
                <MapPin className="h-4 w-4" />
                <span>Retirada no local</span>
              </>
            )}
          </div>
        </div>

        {/* Auto-refresh indicator */}
        <p className="text-center text-xs text-gray-400">
          AtualizaÃ§Ã£o automÃ¡tica a cada 10 segundos
        </p>
      </div>
    </div>
  );
}
